import type Alpine from "alpinejs";

/**
 * True while the global uploader store has any upload in flight. Shared by the
 * navigation guards below and build-skew-guard.ts (which must never force a
 * page load while uploads run — the Upload Resilience Invariant).
 */
export function isAnyUploadActive(alpine: typeof Alpine): boolean {
  const store = alpine.store("uploader") as any;
  return store && typeof store.isAnyUploadActive === "function" ? store.isAnyUploadActive() : false;
}

/**
 * Two layered guards that prevent in-flight uploads from being destroyed by
 * navigation. The HTMX guard is a soft confirm dialog (custom message), the
 * browser guard is a generic "leave site?" prompt that catches everything
 * else (back button, tab close, location.href, etc.).
 *
 * Idempotent: safe to call multiple times; listeners are deduplicated by
 * a window-level flag.
 */
export function registerUploadGuards(alpine: typeof Alpine) {
  if ((window as any).__studioUploadGuardsRegistered) return;
  (window as any).__studioUploadGuardsRegistered = true;

  const isActive = (): boolean => isAnyUploadActive(alpine);

  document.addEventListener("htmx:beforeOnLoad", (evt: any) => {
    const xhr = evt.detail?.xhr;
    if (!xhr) return;
    const willReload =
      xhr.getResponseHeader("HX-Refresh") === "true" || !!xhr.getResponseHeader("HX-Redirect");
    if (!willReload) return;
    if (!isActive()) return;
    const msg =
      (window as any).STUDIO_CONFIG?.uploadManager?.confirm_navigate ||
      "Uploads are in progress. Leaving will cancel them. Continue?";
    // NATIVE confirm on purpose (allowlisted in scripts/lint_window_confirm.sh):
    // htmx:beforeOnLoad needs a synchronous answer — preventDefault must happen
    // inside this dispatch, and the only way to honour HX-Refresh/HX-Redirect
    // after an async themed dialog would be a hand-rolled window.location load.
    if (!window.confirm(msg)) {
      evt.preventDefault();
    }
  });

  window.addEventListener("beforeunload", (e: BeforeUnloadEvent) => {
    if (!isActive()) return;
    e.preventDefault();
    e.returnValue = "";
  });
}
